/**
 * Notebook-Sudoku sound palette.
 *
 * Pencil on paper — filtered brown noise bursts, soft envelopes. The
 * "incorrect" channel doesn't exist; nothing punishes the user. Mistakes
 * just produce a slightly heavier scratch.
 *
 * Every public play* goes through `audio.playIfNotMuted` so the global mute
 * toggle works automatically.
 */

import { audio } from '@/juice';

import type { HapticStyle } from '@rendever/games-contracts';

export type NotebookSoundName =
  | 'pencilScratch'
  | 'pencilScratchLong'
  | 'eraserRub'
  | 'candidateScribble'
  | 'pageRustle'
  | 'pencilTap'
  | 'notebookClose'
  | 'select'
  | 'chime'
  | 'tap';

export const soundHaptic: Partial<Record<NotebookSoundName, HapticStyle>> = {
  pencilScratch: 'light',
  pencilScratchLong: 'medium',
  eraserRub: 'light',
  candidateScribble: 'light',
  pageRustle: 'light',
  pencilTap: 'light',
  notebookClose: 'success',
  select: 'light',
  chime: 'success',
  tap: 'light',
};

// ---------------------------------------------------------------------------
// Building block: brown-ish noise with bandpass shaping — the "scratch" core.
// ---------------------------------------------------------------------------

function makeBrownNoise(ctx: AudioContext, ms: number): AudioBufferSourceNode {
  const buf = audio.noiseBuffer(ctx, { lengthMs: ms, shaping: 1.4 });
  // Low-pass-ish brown bias via a simple integrator across the buffer.
  const data = buf.getChannelData(0);
  let last = 0;
  for (let i = 0; i < data.length; i += 1) {
    last = last * 0.96 + data[i]! * 0.04;
    data[i] = last * 6.5; // gain compensation after integration
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  return src;
}

// ---------------------------------------------------------------------------
// Synthesis
// ---------------------------------------------------------------------------

function synthScratch(long = false): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const dur = long ? 0.22 : 0.13;

  const src = makeBrownNoise(ctx, dur * 1000 + 30);

  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(long ? 1100 : 1450, t);
  bp.frequency.exponentialRampToValueAtTime(long ? 700 : 950, t + dur);
  bp.Q.value = 1.2;

  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 380;
  hp.Q.value = 0.5;

  const { node: g } = audio.envelope(ctx, {
    attack: 0.004,
    decay: 0.02,
    sustain: 0.55,
    release: dur,
    peak: long ? 0.32 : 0.22,
  });

  // Layer a faint pitched bump — the friction "bite" of graphite.
  const osc = audio.makeOscillator(ctx, {
    freq: long ? 240 : 320,
    type: 'triangle',
  });
  osc.frequency.exponentialRampToValueAtTime(long ? 150 : 220, t + dur);
  const oscG = ctx.createGain();
  oscG.gain.setValueAtTime(0.0001, t);
  oscG.gain.exponentialRampToValueAtTime(long ? 0.06 : 0.04, t + 0.006);
  oscG.gain.exponentialRampToValueAtTime(0.0001, t + dur);

  audio.connectThrough(src, hp, bp, g, master);
  audio.connectThrough(osc, oscG, master);
  src.start(t);
  src.stop(t + dur + 0.05);
  osc.start(t);
  osc.stop(t + dur + 0.05);
}

function synthEraserRub(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const dur = 0.26;

  const src = makeBrownNoise(ctx, dur * 1000 + 40);

  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(700, t);
  bp.frequency.linearRampToValueAtTime(550, t + dur);
  bp.Q.value = 0.8;

  // Two "rubs" — modulate gain with a slow LFO-ish envelope to feel like
  // the back-and-forth of an eraser, not a single scrape.
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.28, t + 0.05);
  g.gain.exponentialRampToValueAtTime(0.08, t + 0.13);
  g.gain.exponentialRampToValueAtTime(0.22, t + 0.18);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);

  audio.connectThrough(src, bp, g, master);
  src.start(t);
  src.stop(t + dur + 0.05);
}

function synthCandidateScribble(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const dur = 0.07;

  const src = makeBrownNoise(ctx, dur * 1000 + 20);

  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 1600;

  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 2400;
  bp.Q.value = 1.5;

  const { node: g } = audio.envelope(ctx, {
    attack: 0.002,
    release: dur,
    peak: 0.18,
  });

  audio.connectThrough(src, hp, bp, g, master);
  src.start(t);
  src.stop(t + dur + 0.04);
}

function synthPageRustle(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const dur = 0.45;

  const buf = audio.noiseBuffer(ctx, { lengthMs: 500, shaping: 1.7 });
  const src = ctx.createBufferSource();
  src.buffer = buf;

  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 1100;

  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(2400, t);
  bp.frequency.exponentialRampToValueAtTime(900, t + dur);
  bp.Q.value = 1.0;

  const { node: g } = audio.envelope(ctx, {
    attack: 0.04,
    decay: 0.1,
    sustain: 0.6,
    release: dur,
    peak: 0.22,
  });

  audio.connectThrough(src, hp, bp, g, master);
  src.start(t);
  src.stop(t + dur + 0.05);
}

function synthPencilTap(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const dur = 0.08;

  const osc = audio.makeOscillator(ctx, { freq: 1800, type: 'sine' });
  osc.frequency.exponentialRampToValueAtTime(900, t + dur);
  const { node: g } = audio.envelope(ctx, {
    attack: 0.001,
    release: dur,
    peak: 0.18,
  });
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + dur + 0.02);

  // Tactile click layer.
  const buf = audio.noiseBuffer(ctx, { lengthMs: 30 });
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 1800;
  const { node: ng } = audio.envelope(ctx, {
    attack: 0.001,
    release: 0.03,
    peak: 0.16,
  });
  audio.connectThrough(src, hp, ng, master);
  src.start(t);
  src.stop(t + 0.04);
}

function synthNotebookClose(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;

  // Soft low thump — the "page closes" body.
  const osc = audio.makeOscillator(ctx, { freq: 140, type: 'sine' });
  osc.frequency.exponentialRampToValueAtTime(70, t + 0.35);
  const { node: g } = audio.envelope(ctx, {
    attack: 0.01,
    decay: 0.06,
    sustain: 0.5,
    release: 0.4,
    peak: 0.42,
  });
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 600;
  audio.connectThrough(osc, lp, g, master);
  osc.start(t);
  osc.stop(t + 0.55);

  // Layered shuffle — a small page-edge whisper at the end.
  const buf = audio.noiseBuffer(ctx, { lengthMs: 350, shaping: 1.5 });
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(1800, t);
  bp.frequency.exponentialRampToValueAtTime(600, t + 0.35);
  bp.Q.value = 0.9;
  const { node: ng } = audio.envelope(ctx, {
    attack: 0.02,
    decay: 0.1,
    sustain: 0.5,
    release: 0.3,
    peak: 0.18,
  });
  audio.connectThrough(src, bp, ng, master);
  src.start(t + 0.05);
  src.stop(t + 0.45);
}

function synthChime(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  // F#5 + A5 — soft, paper-feeling
  const notes = [739.99, 880];
  notes.forEach((freq, i) => {
    const osc = audio.makeOscillator(ctx, { freq, type: 'sine' });
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t + i * 0.06);
    g.gain.exponentialRampToValueAtTime(0.14, t + i * 0.06 + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + i * 0.06 + 0.9);
    audio.connectThrough(osc, g, master);
    osc.start(t + i * 0.06);
    osc.stop(t + i * 0.06 + 1.0);
  });
}

function synthSelect(): void {
  // A short tap + a tiny scratch — the "selected a cell" affordance.
  synthPencilTap();
}

// ---------------------------------------------------------------------------
// Public controller
// ---------------------------------------------------------------------------

class NotebookSound {
  unlock(): void {
    audio.unlockAudio();
  }

  play(name: NotebookSoundName): void {
    audio.playIfNotMuted(name, () => {
      audio.unlockAudio();
      switch (name) {
        case 'pencilScratch':
          synthScratch(false);
          return;
        case 'pencilScratchLong':
          synthScratch(true);
          return;
        case 'eraserRub':
          synthEraserRub();
          return;
        case 'candidateScribble':
          synthCandidateScribble();
          return;
        case 'pageRustle':
          synthPageRustle();
          return;
        case 'pencilTap':
          synthPencilTap();
          return;
        case 'notebookClose':
          synthNotebookClose();
          return;
        case 'select':
          synthSelect();
          return;
        case 'chime':
          synthChime();
          return;
        case 'tap':
          synthPencilTap();
          return;
      }
    });
  }
}

export const sound = new NotebookSound();
